import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/features/auth/AuthContext';
import { reportError } from '@/lib/reportError';

export type FastTrackMode = 'live' | 'recorded';

export const FAST_TRACK_WEEK_COUNT = 5;

export interface FastTrackConfig {
  /** How Fast Track is being delivered right now. Global, admin-controlled. */
  mode: FastTrackMode;
  /** Vimeo player URLs for the five recorded sessions. Index = week - 1. */
  recordings: string[];
  anchorDate: string;
  /** Zoom links per cohort (live mode). Index = cohort - 1. */
  links: string[];
  /** Display time strings per cohort (live mode). Index = cohort - 1. */
  times: string[];
}

export interface FastTrackConfigResult extends FastTrackConfig {
  loading: boolean;
}

const EMPTY: FastTrackConfig = {
  mode: 'live',
  recordings: [],
  anchorDate: '',
  links: [],
  times: [],
};

/** Coerce a jsonb column that should be an array of strings. */
function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => (typeof v === 'string' ? v : ''));
}

/**
 * Reads the fast_track_config singleton. Read-only and side-effect free, unlike
 * useCohort (which will auto-assign and persist a cohort). Any surface that just
 * needs to know the mode or the recording URLs should use this.
 */
export function useFastTrackConfig(): FastTrackConfigResult {
  const { session } = useAuth();
  const userId = session?.user?.id;
  const [config, setConfig] = useState<FastTrackConfig>(EMPTY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const load = async () => {
      const { data, error } = await supabase
        .from('fast_track_config')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        reportError(error, { tool: 'fast_track', endpoint: 'fast_track_config' });
      } else if (data) {
        setConfig({
          mode: data.mode === 'recorded' ? 'recorded' : 'live',
          recordings: toStringArray(data.recordings),
          anchorDate: data.anchor_date,
          links: toStringArray(data.cohort_links),
          times: toStringArray(data.cohort_times),
        });
      }
      setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, [userId]);

  return { ...config, loading };
}
